import {Dispatch} from 'redux'
import {trackEventsBatch} from 'setup/redux/Actions/Onboarding/trackEventsBatch'
import {trackTimetableEvent} from 'setup/redux/Actions/Timetable/timetable'

import {detectDeviceType} from './deviceType'
import {sendWithRetry} from './trackingRetry'

type OnboardingEventName =
  | 'data_selection'
  | 'data_clear'
  | 'onboarding'
  // The branch-dependent tail of the wizard only (Assign Subjects tabs + Summary).
  // Positions 1–13 (mode → data-input) are derived server-side from the preferences
  // pointer — never emit them from the portal (see ONBOARDING_SUBSTEP_TRACKING spec §A2).
  | 'onboarding_substep_final'
  // ONE carve-out from the rule above: `mode`, and nothing else on this event.
  //
  // Positions 2–13 are still server-derived, and correctly so — the pointer only
  // reaches them when the user advances. Position 1 is different: the wizard MOUNTS
  // there, behind the Welcome modal, so the pointer says "Mode Selection" for every
  // tenant that merely lands on /onboarding, including the sample-data-only ones who
  // never see the screen. node-api now ignores a pointer that has never left position
  // 1 (onboardingSubstepSignals.resolveReachedSignals), and this is the witness that
  // replaces it — fired from the one place that knows the screen is actually visible.
  | 'onboarding_substep'
  // The funnel card ticks a linear screen on its `_saved` row, not its arrival row. Those
  // rows are normally derived server-side from the preferences pointer write, but the
  // collapsed Setup Basics page hosts the curriculum picker on the pointer's FIRST
  // position, so its `curriculum_curriculum` save was only ever swept up by a later
  // screen's flush (minutes late, or never). The portal now states that one save
  // explicitly in the Setup Basics batch. Metadata carries `pointer_index: 1` — NOT 2:
  // the legacy v1 card still reads pointer_index against the old numbering, and a 2
  // would falsely tick Branches. The only other portal emitters are the two Teachers
  // screens of Step 4 (9 Sep 2026): their tick keys on this row too, and node-api only
  // writes it on the teachers bulk import, which runs on Review's Continue — so the
  // wizard states the save on the click that leaves each screen (see handleTeacherReview
  // / handleTeacherReviewConfirm in OnboardingWizard/index.tsx). Nothing else may.
  | 'onboarding_substep_saved'
  // THE USER CHANGED THIS SCREEN'S DATA, as opposed to accepting the wizard's prefill. The
  // second, independent witness beside `_saved`: the wizard now pre-fills most of itself, and
  // a school that pressed Next four times is indistinguishable from one that typed every
  // field without it. One row per edited position, appended LAST to the batch of the Next
  // click that completes the main step, and emitted from nowhere else (never on unmount or
  // visibilitychange) so that edited(s) is a subset of completed(s) and the backend's Edit
  // Stats identity holds (ONBOARDING_EDIT_TRACKING spec section 2).
  | 'onboarding_substep_edited'
  // Leaving the wizard through a Summary CTA — the user is about to see the app proper.
  | 'dashboard'
  // How teachers were allocated on the Lesson Planning tab. Its own event, NOT
  // an onboarding_substep_final metadata rider: that family is deduped once per
  // (tenant, substep) server-side, so method repeats would never be stored.
  | 'teacher_allocation'

type OnboardingEventSubstep =
  | 'mode'
  // Linear funnel positions whose screens the collapsed wizard folded into one
  // UI. The preferences pointer can no longer witness them individually — the
  // user never "sits" on them, so the server-side derivation could only
  // ordinal-backfill them as inferred. Each page reports the positions ITS UI
  // hosts in one ordered batch when it completes (ONBOARDING_BULK_EVENTS spec):
  //  - Setup Basics -> mode + curriculum_curriculum (the picker moved there);
  //  - Institution Setup (one review page) -> the five core_* positions;
  //  - Academic Structure (one review page) -> levels, classes, sections, review, subjects.
  | 'core_branches'
  | 'core_duration'
  | 'core_frequency'
  | 'core_venues'
  | 'core_timings'
  | 'curriculum_curriculum'
  | 'curriculum_levels'
  | 'curriculum_classes'
  | 'curriculum_sections'
  | 'curriculum_review'
  | 'curriculum_subjects'
  | 'actual_data'
  | 'sample_data'
  // Delete pressed in DeleteSampleDataModal (click time). The confirmed clear,
  // `sample_data_clear`, is emitted by node-api after the delete succeeds and is
  // deliberately NOT in this union — the portal must never fire it.
  | 'sample_data_clear_clicked'
  | 'onboarding_started'
  | 'assign_teachers'
  | 'assign_review_teachers'
  | 'assign_students'
  | 'assign_lesson_planning'
  | 'summary'
  | 'first_opened'
  // teacher_allocation substeps — one per allocation method, not a funnel.
  | 'manual_assignment'
  | 'ai_assignment'
  | 'excel_assignment'
  | 'ai_opened_not_used'

/** One entry of an ordered batch for `trackOnboardingEvents`. */
export type OnboardingBatchEvent = {
  event_name: OnboardingEventName
  substep: OnboardingEventSubstep
  metadata?: Record<string, unknown> | null
}

// Server-side STATS_MAX_EVENTS_PER_BATCH — a larger batch 400s with NOTHING
// recorded, so the helper chunks defensively before dispatching.
const MAX_EVENTS_PER_BATCH = 25

const makeBatchId = (): string => {
  try {
    return crypto.randomUUID()
  } catch {
    return `b-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  }
}

/**
 * Every tracking POST from this module goes through ONE chain, so a call that is waiting out
 * a 429 (see trackingRetry.ts) cannot be overtaken by a later one. The funnel writer
 * synthesises earlier substeps from arrival order, so the order of separate calls is data
 * too, not only the order inside a batch. It also stops a Next click's burst of POSTs from
 * racing each other into the rate limiter in the first place. The chain never rejects: every
 * sender resolves on failure as well as success.
 */
let trackingChain: Promise<void> = Promise.resolve()
const enqueueTracking = (work: () => Promise<void>): Promise<void> => {
  const run = trackingChain.then(work, work)
  trackingChain = run
  return run
}

/**
 * Batched sibling of `trackOnboardingEvent` — POST onboarding/track-events.
 *
 * THE ORDER OF `events` IS DATA (spec §2): the subscription service applies the
 * array sequentially and synthesises earlier substeps of the funnel from it, so
 * pass events in the order the user completed them and never sort, dedupe or
 * re-key the array. Batches over the 25-event server cap are split into ordered
 * chunks sent one after another (the second only after the first resolves), so
 * order holds across the split too.
 *
 * `device_type` rides as batch-level metadata — every dashboard device filter
 * keys on it, and per-event copies are merged over it (event wins). Like the
 * single-event helper, this resolves on failure as well as success: tracking
 * must never block a navigation. Repeats come back `deduped`, which is a
 * success — the server owns dedupe state, don't mirror it client-side.
 *
 * A chunk rejected with 429 (or another transient failure) is re-sent with the
 * SAME batch_id and body after a backoff, see trackingRetry.ts; the next chunk
 * waits for it, so the order still holds.
 */
export const trackOnboardingEvents = (
  dispatch: Dispatch,
  urddId: string | number | null | undefined,
  events: OnboardingBatchEvent[]
): Promise<void> => {
  if (events.length === 0) return Promise.resolve()

  const chunks: OnboardingBatchEvent[][] = []
  for (let i = 0; i < events.length; i += MAX_EVENTS_PER_BATCH) {
    chunks.push(events.slice(i, i + MAX_EVENTS_PER_BATCH))
  }

  const sendChunk = (chunk: OnboardingBatchEvent[]): Promise<void> => {
    // Built once per chunk so every retry re-sends the identical batch.
    const body = {
      batch_id: makeBatchId(),
      urdd_id: urddId ? Number(urddId) : null,
      metadata: {device_type: detectDeviceType()},
      events: chunk,
    }
    return sendWithRetry((onSuccess, onFailure) => {
      dispatch(trackEventsBatch(body, onSuccess, onFailure) as never)
    })
  }

  return enqueueTracking(() =>
    chunks.reduce((prev, chunk) => prev.then(() => sendChunk(chunk)), Promise.resolve())
  )
}

export const trackOnboardingEvent = (
  dispatch: Dispatch,
  urddId: string | number | null | undefined,
  eventName: OnboardingEventName,
  substep: OnboardingEventSubstep,
  metadata: Record<string, unknown> | null = null
): Promise<void> => {
  const body = {
    urdd_id: urddId ? Number(urddId) : null,
    event_name: eventName,
    substep,
    processing_time: null,
    metadata,
  }
  return enqueueTracking(() =>
    sendWithRetry((onSuccess, onFailure) => {
      dispatch(trackTimetableEvent(body, onSuccess, onFailure) as never)
    })
  )
}
